import { effect, inject, signal, untracked, DestroyRef, Service } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Api } from '@api/api';
import { addWishlistItem, deleteItemFromWishlist } from '@api/functions';
import { CustomerService } from './customer.service';
import { DeviceService } from './device-detector/device.service';
import { GoogleAnalyticsService, ProductAnalyticsData } from './google-analytics.service';

@Service()
export class WishlistService {
  private destroyRef = inject(DestroyRef);
  private readonly customerService = inject(CustomerService);
  private readonly api = inject(Api);
  private readonly deviceService = inject(DeviceService);
  private readonly googleAnalyticsService = inject(GoogleAnalyticsService);

  private readonly persistenceKey = 'kl.wishlist';

  readonly wishlistProductIds = signal<number[]>([]);

  constructor() {
    if (!this.deviceService.isPlatformBrowser()) {
      return;
    }

    this.initWishlist();

    effect(() => {
      const wishlistProductIds = this.wishlistProductIds();
      untracked(() => {
        this.persistWishlistProductIds(wishlistProductIds);
      });
    });

    effect(() => {
      this.customerService.customer();
      untracked(() => {
        this.mergeCustomerWishlistProductIds();
      });
    });
  }

  private initWishlist(): void {
    const persistedWishlistProductIds = this.getWishlistProductIdsFromPersistence();
    this.wishlistProductIds.set(persistedWishlistProductIds);
  }

  isInWishlist(productId: number): boolean {
    return this.wishlistProductIds().includes(productId);
  }

  toggleWishlistItem(
    productId: number,
    analyticsData: ProductAnalyticsData,
  ): void {
    if (this.isInWishlist(productId)) {
      this.removeFromWishlist(productId, analyticsData);
    } else {
      this.addToWishlist(productId, analyticsData);
    }
  }

  addToWishlist(
    productId: number,
    analyticsData: ProductAnalyticsData,
  ): void {
    this.wishlistProductIds.update(wishlistProductIds => this.mergeProductIds(wishlistProductIds, [productId]));
    this.api.invoke(addWishlistItem, { productId })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({});

    this.googleAnalyticsService.addToWishlist(analyticsData);
  }

  removeFromWishlist(
    productId: number,
    analyticsData: ProductAnalyticsData,
  ): void {
    this.wishlistProductIds.update(wishlistProductIds => wishlistProductIds.filter(id => id !== productId));
    this.api.invoke(deleteItemFromWishlist, { productId })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({});

    this.googleAnalyticsService.removeFromWishlist(analyticsData);
  }

  private mergeCustomerWishlistProductIds(): void {
    if (!this.customerService.customer()) {
      return;
    }
    const customerWishlistProductIds: number[] = this.customerService.customer()?.wishlistProductIds || [];
    const persistedWishlistProductIds: number[] = this.getWishlistProductIdsFromPersistence();
    const mergedWishlistProductIds = this.mergeProductIds(
      persistedWishlistProductIds,
      customerWishlistProductIds,
    );
    this.wishlistProductIds.set(mergedWishlistProductIds);
  }

  private persistWishlistProductIds(wishlistProductIds: number[]): void {
    if (!this.deviceService.isPlatformBrowser()) {
      return;
    }
    localStorage.setItem(this.persistenceKey, JSON.stringify(wishlistProductIds));
  }

  private getWishlistProductIdsFromPersistence(): number[]  {
    if (!this.deviceService.isPlatformBrowser()) {
      return [];
    }
    const persistedWishlistProductIds: string = localStorage.getItem(this.persistenceKey);
    return persistedWishlistProductIds ? JSON.parse(persistedWishlistProductIds) : [];
  }

  private mergeProductIds(wishlistProductIds1: number[], wishlistProductIds2: number[]): number[] {
    const wishlistProductIdsSet = new Set([...wishlistProductIds1, ...wishlistProductIds2]);
    return Array.from(wishlistProductIdsSet);
  }
}
